import { lazy, Suspense, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../auth/AuthProvider';
import { RemoteErrorBoundary } from '../components/RemoteErrorBoundary';
import { useT } from '../i18n';

/** The federated Platform Admin console, loaded on demand at /admin. */
const RemoteAdminApp = lazy(() => import('workbench/AdminApp'));

/**
 * Platform Admin landing: ADMIN-role logins are routed here from the root. The
 * console renders full-viewport (its own sidebar and topbar) — the shell
 * chrome stays underneath, matching the prototype's admin persona.
 */
export function AdminRoute() {
  const { user, loading, login, logout, roles } = useAuth();
  const t = useT();

  // No anonymous surface: unauthenticated visits (including the moment after
  // Sign out, while the browser leaves for Keycloak) show only a spinner.
  useEffect(() => {
    if (!loading && !user) {
      login();
    }
  }, [loading, user, login]);

  if (loading) {
    return (
      <Box className="flex justify-center py-12">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box className="flex justify-center py-12">
        <CircularProgress />
      </Box>
    );
  }

  if (!roles.includes('ADMIN')) {
    return (
      <Alert severity="warning">
        {t('🔒 The Platform Admin console requires platform-admin authority (ADMIN role).')}
      </Alert>
    );
  }

  return (
    <RemoteErrorBoundary fallbackMessage={t('Feature unavailable — is workbench running?')}>
      <Suspense
        fallback={
          <Box className="flex justify-center py-12">
            <CircularProgress />
          </Box>
        }
      >
        <RemoteAdminApp
          userName={
            (user.profile.name as string | undefined) ??
            (user.profile.preferred_username as string | undefined) ??
            user.profile.email ??
            undefined
          }
          userEmail={user.profile.email}
          onSignOut={logout}
        />
      </Suspense>
    </RemoteErrorBoundary>
  );
}
